import { AddOnAvailability, AddOnType, GetAddOnResponse } from '../../types';
import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators';

import { StateController } from '../../helpers/state/state-management/state-controller';
import accountInfo from '../../helpers/state/account-info';
import topicData from '../../helpers/state/topic-data';

@customElement('admin-addons-list-fragment')
export class AdminAddonsListFragment extends LitElement {
    private readonly amplitude_callsite: string = 'Addons Fragment';

    @state()
    selectedAddon?: GetAddOnResponse;

    @state()
    activeTab: string = 'discoverAddons';

    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindTopicState = new StateController(this, topicData);

    addOnSortOrder: AddOnType[] = [
        AddOnType.CustomForm,
        AddOnType.Challenge,
        AddOnType.Guide,
        AddOnType.BestOf,
        AddOnType.LeadPointForm,
        AddOnType.Paywall,
        AddOnType.Password,
        AddOnType.ScavengerHunt,
        AddOnType.IntegrationAutomation,
        AddOnType.EmailGate,
        AddOnType.LeadPopUp,
        AddOnType.Comments,
        AddOnType.Zapier,
        AddOnType.Translation,
        AddOnType.KmlUpload,
    ];

    private setActiveAddon(addon: GetAddOnResponse) {
        this.selectedAddon = addon;
    }

    private getAddonStatus(addon: GetAddOnResponse) {
        switch (addon.availability) {
            case AddOnAvailability.GetNotified:
                return 'Coming Soon';
            case AddOnAvailability.Available:
                return '';
            case AddOnAvailability.ComingSoon:
                return 'In Development';
            default:
                return 'Learn More';
        }
    }

    private unsetActiveAddon() {
        this.selectedAddon = undefined;
    }

    private async openUpgradeDialog(e: CustomEvent) {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: e.detail,
            })
        );
    }


    private switchTab(tab: string) {
        this.activeTab = tab;
    }



    protected render() {
        return html`
            ${this.selectedAddon ? html`
                <admin-addon-renderer
                    @close-addon=${this.unsetActiveAddon}
                    @open-upgrade-dialog=${this.openUpgradeDialog}
                    .selectedAddon=${this.selectedAddon}>
                </admin-addon-renderer>`
            : html`
                <div class="addon__container">
                    <div class="tabs">
                        <div class="tab ${this.activeTab === 'discoverAddons' ? 'active' : ''}" @click=${() => this.switchTab('discoverAddons')}>Discover</div>
                        <div class="tab ${this.activeTab === 'yourAddons' ? 'active' : ''}" @click=${() => this.switchTab('yourAddons')}>Your Add-ons</div>
                    </div>

                    ${topicData.activatedAddons.length > 0 && this.activeTab === 'yourAddons' ? html`
                        <div class="addons__wrapper">
                            ${topicData.activatedAddons.map(addon => html`
                            <div class="addon__preview" @click="${() => this.setActiveAddon(addon)}">
                                <div class="addon__preview-title-wrapper"  @click="${() => this.setActiveAddon(addon)}">
                                    <div class="addon__preview-image-wrapper-inactive">
                                        <div class="addon__preview-image-wrapper">
                                            <div class="addon_icon-holder">
                                                <mwc-icon class="addon__icon">${addon.icon_path}</mwc-icon>
                                            </div>
                                            <img class="addon__preview-image-background" src="/images/addon-icons/addonbackground.png"/>
                                        </div>
                                    </div>
                                    <div class="addon__preview-details-wrapper-inactive">
                                        <h4 class="addon__preview-title">
                                            ${addon.title}
                                        </h4>
                                        <p class="addon__preview-supporting-text">From <b>${addon.owner}</b> <i>${this.getAddonStatus(addon) !== '' ? `(${this.getAddonStatus(addon)})` : ''}</i></p>
                                    </div>
                                </div>
                            </div>`
                            )}
                        </div>`
                    : ''
                    }

                    ${topicData.unactivatedAddons.length > 0 && this.activeTab === 'discoverAddons' ? html`
                        <div class="addons__wrapper">
                            ${topicData.unactivatedAddons
                                .sort((a, b) => {
                                    const order = [AddOnAvailability.Available, AddOnAvailability.ComingSoon, AddOnAvailability.GetNotified];
                                    return order.indexOf(a.availability) - order.indexOf(b.availability);
                                  })
                                .sort((a, b) => this.addOnSortOrder.indexOf(a.component) - this.addOnSortOrder.indexOf(b.component))
                                .map(addon => html`
                                <div class="addon__preview" @click="${() => this.setActiveAddon(addon)}">
                                    <div class="addon__preview-title-wrapper">
                                        <div class="addon__preview-image-wrapper-inactive">
                                            <div class="addon__preview-image-wrapper">
                                                <div class="addon_icon-holder">
                                                    <mwc-icon class="addon__icon">${addon.icon_path}</mwc-icon>
                                                </div>
                                                <img class="addon__preview-image-background" src="/images/addon-icons/addonbackground.png"/>
                                            </div>
                                        </div>
                                        <div class="addon__preview-details-wrapper-inactive">
                                            <h4 class="addon__preview-title">${addon.title}</h4>
                                        </div>
                                    </div>
                                </div>`
                            )}
                        </div>
                    ` : ''
                    }
                </div>`
            }

            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            /* width */
            .addon__container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .addon__container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .addon__container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .tabs {
                display: flex;
                cursor: pointer;
            }
            .tab {
                padding: 5px 10px;
                flex: 1;
                text-align: center;
            }
            .tab.active {
                border-bottom: 2px solid var(--main-secondary-color);
                font-weight: bold;
            }
            .tab-content {
                border: 1px solid #ccc;
                padding: 10px;
            }

            .addon__container {
                overflow: hidden auto;
                padding-right: 20px;
            }

            .addons__wrapper {
                display: flex;
                flex-wrap: wrap;
                justify-content: left;
            }

            .addon__preview-title {
                margin: 0;
                font-size: 0.7rem;
                font-weight: 700;
                cursor: pointer;
            }

            .addon__preview-supporting-text {
                font-size: 0.65rem;
                margin-top: 2px;
                margin-bottom: 0;
                color: var(--theme-input-color);
            }

            .addon__preview-title-wrapper {
                display: flex;
                align-items: center;
            }

            .addon__preview-image-wrapper-active {
                width: 30%;
                hover: pointer;
            }

            .addon_icon-holder {
                width: 0;
            }

            .addon__preview-image-wrapper {
                height: 50px;
                display: flex;
                justify-content: center;
            }

            .addon__preview-details-wrapper-inactive {
                width: 30%;
                flex-grow: 1;
                margin-left: 10px;
            }

            .addon__preview {
                padding: 10px 0;
                display: flex;
                align-items: center;
                width: 100%;
                justify-content: space-between;
                cursor: pointer;
            }

            .addon__icon {
                position: relative;
                left: 17px;
                top: 17px;
                --mdc-icon-size: 16px;
            }

            .addon__preview-image {
                height: 40px;
                margin-top: 20px;
                position: absolute;
                cursor: pointer;
            }

            .addon__preview-image-background {
                height: 100%
            }

            .addon__preview-upgrade-icon {
                height: 26px;
            }

            .addon__preview--active {
                max-width: 25%;
                padding: 10px;
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                font-size: 0.7rem;

            }
        `;
    }
}
